import AnalyticsOutlinedIcon from "@mui/icons-material/AnalyticsOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import OutboxOutlinedIcon from "@mui/icons-material/OutboxOutlined";
import PollOutlinedIcon from "@mui/icons-material/PollOutlined";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import { Stack } from "@mui/material";
import { type FC, useEffect } from "react";
import { useLocation, useNavigate } from "react-router";
import { RouteTabs } from "../../modules/generic/components/route-tabs";
import type { Tags } from "../../modules/merge-tag/types";
import type { MomentFormArgs } from "../../modules/moment/component/moment-form";
import { MomentFormInner } from "../../modules/moment/component/moment-form-inner";
import type { ProgramsState } from "../../modules/moment/hooks/use-programs";
import { usePermissions } from "../../server/auth/use-permissions";
import type { UUID } from "../../utils/uuid";
import { MomentAnalyticsTab } from "./analytics/moment-analytics-tab";
import { MomentOutboxTab } from "./analytics/moment-outbox-tab";
import { MomentPollsTab } from "./polls/moment-polls-tab";
import { MomentReview } from "./review/moment-review";

const analyticsQueryParams = ["view", "dimension"];

const DefaultPage: FC<{ id: UUID; isNewsletter: boolean }> = ({ id, isNewsletter }) => {
  const navigate = useNavigate();
  const { search } = useLocation();
  useEffect(() => {
    void navigate(
      {
        pathname: `/moments/${id}/${isNewsletter ? "newsletter/" : ""}details`,
        search,
      },
      { replace: true },
    );
  }, [id, navigate, isNewsletter, search]);
  return null;
};

export const MomentFormPageTabs: FC<
  MomentFormArgs & {
    isNew: boolean;
    reviewExperienceEnabled: boolean;
    onSaved: () => void;
    mergeTags: Tags;
    reloadMergeTags: () => void;
    saveDataReady: boolean;
    programs: ProgramsState;
  }
> = ({
  moment,
  isNew,
  reviewExperienceEnabled,
  onSaved,
  isNewsletter,
  saveDataReady,
  programs,
  ...rest
}) => {
  const { ce_admin } = usePermissions();

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const basePath = `/moments/${moment.id}/${isNewsletter ? "newsletter" : ""}`.replace(/\/$/, "");
  const detailsPath = `/moments/${moment.id}/${isNewsletter ? "newsletter/" : ""}details`;

  useEffect(() => {
    if (moment.follow_up_id) {
      void navigate(`/follow-up/${moment.follow_up_id}`, { replace: true });
    }
  }, [moment.follow_up_id, navigate]);

  useEffect(() => {
    if (reviewExperienceEnabled && !isNew && pathname === basePath) {
      void navigate(detailsPath, { replace: true });
    }
  }, [basePath, detailsPath, isNew, navigate, pathname, reviewExperienceEnabled]);

  const details = (
    <MomentFormInner
      moment={moment}
      isNewsletter={isNewsletter}
      saveDataReady={saveDataReady}
      programs={programs}
      onSaved={onSaved}
      {...rest}
    />
  );

  if (!reviewExperienceEnabled && moment.status === "draft") return details;

  return (
    <RouteTabs
      appearance={reviewExperienceEnabled ? "toolbar" : "default"}
      showTabs={!reviewExperienceEnabled || !isNew}
      renderFirstTabAtBase={reviewExperienceEnabled}
      base={`/moments/${moment.id}/${isNewsletter ? "newsletter/" : ""}`}
      defaultPage={
        reviewExperienceEnabled && isNew ? (
          details
        ) : (
          <DefaultPage id={moment.id} isNewsletter={!!isNewsletter} />
        )
      }
      preserveQuery
      tabs={[
        ...(reviewExperienceEnabled
          ? [
              {
                type: "explicit" as const,
                data: {
                  name: "Edit",
                  route: "details",
                  icon: <EditOutlinedIcon />,
                  queryParamsToRemove: analyticsQueryParams,
                },
                component: details,
              },
            ]
          : [
              {
                type: "inferred" as const,
                data: { name: "Details", queryParamsToRemove: analyticsQueryParams },
                component: details,
              },
            ]),
        ...(reviewExperienceEnabled
          ? [
              {
                type: "inferred" as const,
                data: {
                  name: "Review",
                  icon: <RateReviewOutlinedIcon />,
                  fullBleed: true,
                  queryParamsToRemove: analyticsQueryParams,
                },
                component: (
                  <MomentReview
                    momentId={moment.id}
                    isNewsletter={isNewsletter}
                    onClose={() => void navigate(detailsPath)}
                  />
                ),
              },
            ]
          : []),
        ...(moment.status === "draft"
          ? []
          : [
              {
                type: "inferred" as const,
                data: {
                  name: "Analytics",
                  icon: reviewExperienceEnabled ? <AnalyticsOutlinedIcon /> : undefined,
                },
                component: (
                  <Stack direction="column" spacing={8}>
                    <MomentAnalyticsTab moment={moment} />
                  </Stack>
                ),
              },
              ...(moment.poll_ids?.length
                ? [
                    {
                      type: "inferred" as const,
                      data: {
                        name: "Poll Results",
                        icon: reviewExperienceEnabled ? <PollOutlinedIcon /> : undefined,
                        queryParamsToRemove: analyticsQueryParams,
                      },
                      component: (
                        <Stack direction="column" spacing={8}>
                          <MomentPollsTab moment={moment} />
                        </Stack>
                      ),
                    },
                  ]
                : []),
              ...(ce_admin
                ? [
                    {
                      type: "inferred" as const,
                      data: {
                        name: "Outbox",
                        icon: reviewExperienceEnabled ? <OutboxOutlinedIcon /> : undefined,
                        queryParamsToRemove: analyticsQueryParams,
                      },
                      component: (
                        <Stack direction="column" spacing={8}>
                          <MomentOutboxTab moment={moment} />
                        </Stack>
                      ),
                    },
                  ]
                : []),
            ]),
      ]}
    />
  );
};
